import React from 'react';
import './PrivacyPolicy.css';
import { Link, useLocation } from 'react-router-dom';

function PrivacyPolicy() {
  const effectiveDate = 'February 20, 2026';
  const appName = 'GlowAI';
  const location = useLocation();

  return (
    <div className="privacy-policy">
      <header className="privacy-header">
        <div className="header-content">
          <h1>Privacy Policy</h1>
          <p className="subtitle">How we handle your data</p>
        </div>
      </header>

      <main className="privacy-content">
        <p className="effective-date">Effective Date: {effectiveDate}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to {appName}. We are committed to protecting your privacy
            and ensuring that your personal information is handled in a safe and
            responsible manner. This Privacy Policy explains how we collect, use,
            and protect your information when you use our mobile application.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>

          <h3>2.1 Information You Provide</h3>
          <ul>
            <li>Skin care routine preferences and schedules you create</li>
            <li>Notification preferences and reminder settings</li>
            <li>Photos of your skin for AI analysis (if you choose to submit them)</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li>Device type and operating system version</li>
            <li>App usage statistics and crash reports</li>
            <li>General location data (for personalized recommendations, if enabled)</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide and maintain the app's core functionality</li>
            <li>Send you skin care reminders and notifications</li>
            <li>Improve and optimize the AI skin analysis experience</li>
            <li>Provide personalized skin care recommendations</li>
            <li>Diagnose technical issues and fix bugs</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          <p>
            Your skin care data and preferences are stored locally on your device.
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction.
          </p>
        </section>

        <section>
          <h2>5. Camera and Photo Access</h2>
          <p>
            {appName} may request access to your device's camera and photo library
            to enable AI-powered skin analysis. Photos used for analysis are
            processed securely and are not shared with third parties without your
            explicit consent. You can revoke camera and photo permissions at any
            time through your device settings.
          </p>
        </section>

        <section>
          <h2>6. Third-Party Services</h2>
          <p>
            Our app may use third-party services that collect information used to
            identify you. These may include:
          </p>
          <ul>
            <li>Firebase (for crash reporting and analytics)</li>
            <li>AI analysis services (for skin assessment)</li>
          </ul>
          <p>
            Each third-party service has its own privacy policy governing the use
            of your information.
          </p>
        </section>

        <section>
          <h2>7. Notifications</h2>
          <p>
            {appName} sends local notifications to remind you about your skin care
            routine tasks. You can manage or disable these notifications at any time
            through your device settings or within the app.
          </p>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>
            Our app does not knowingly collect personal information from children
            under the age of 13. If you are a parent or guardian and believe your
            child has provided us with personal information, please contact us so
            we can take appropriate action.
          </p>
        </section>

        <section>
          <h2>9. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Opt out of analytics data collection</li>
            <li>Export your skin care data</li>
          </ul>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy within the app
            and updating the effective date above. You are advised to review this
            Privacy Policy periodically for any changes.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy,
            please contact us at:
          </p>
          <p className="contact-email">
            <Link
              to="/contact"
              className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
            >
              Contact Us
            </Link>
          </p>
        </section>
      </main>

      <footer className="privacy-footer">
        <p>&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
